import React, { useCallback, useEffect, useState } from 'react'
import { DigitalAsset, DigitalAssetListRequestFilter, DraftDigitalAsset } from 'modules/domain/digitalAsset/types'
import DigitalAssetSelectors from 'modules/domain/digitalAsset/selectors'
import DigitalAssetActions from 'modules/domain/digitalAsset/duck'
import { useDigitalAssetList } from 'modules/domain/digitalAsset/hooks'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Status } from 'types/entities'
import { useAction, useDidMount } from '@agro-club/frontend-shared'
import type { GetProp, TableProps } from 'antd'
import { Table } from 'antd'
import { Progress } from '../../../../modules/types'
import { SorterResult } from 'antd/es/table/interface'
import { formatIsoDate } from '../../../../modules/utils/helpers'
import DADropdownActions from '../../../components/DigitalAssets/DADropdownActions'
import '../styles.css'

type ColumnsType<T extends object = object> = TableProps<T>['columns']
type TablePaginationConfig = Exclude<GetProp<TableProps, 'pagination'>, boolean>

type TableParams = {
  pagination?: TablePaginationConfig
  sorting?: SorterResult<any>['field']
  ordering?: SorterResult<any>['order']
  filters?: Parameters<GetProp<TableProps, 'onChange'>>[1]
  sortOrder?: SorterResult<any>['order']
  sortField?: SorterResult<any>['field']
}

type DigitalAssetTableProps = {
  setDigitalAssetDetail?: (asset: DigitalAsset) => void
  height?: number
  canEditOrDelete: boolean
}

const DigitalAssetTable: React.FC<DigitalAssetTableProps> = ({ setDigitalAssetDetail, height, canEditOrDelete }) => {
  const { t } = useTranslation(['digitalAsset', 'labels'])
  const [progress, data = []] = useDigitalAssetList({})
  const filterUpdated = useAction(DigitalAssetActions.filterUpdated)
  const listRequested = useAction(DigitalAssetActions.listRequested)
  const sortingUpdated = useAction(DigitalAssetActions.sortingUpdated)
  const filterValue = useSelector(DigitalAssetSelectors.filter)
  const total = useSelector(DigitalAssetSelectors.total)
  const page = useSelector(DigitalAssetSelectors.page)
  const pageSize = useSelector(DigitalAssetSelectors.pageSize)
  let { document_type, status } = filterValue
  const { sort_field, sort_reversed } = useSelector(DigitalAssetSelectors.sorting)
  const selectedDigitalAssetDraftAction = useAction(DigitalAssetActions.selectedFileDraftRequested)
  const draftDigitalAssets = useSelector(DigitalAssetSelectors.drafts)
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      showSizeChanger: false,
      position: ['bottomLeft'],
    },
  })
  const [selectedRows, setSelectedRows] = useState<React.Key[] | undefined>(undefined)

  useEffect(() => {
    setTableParams((old) => ({
      ...old,
      pagination: {
        ...old.pagination,
        current: page,
        pageSize,
        total,
      },
    }))
  }, [page, pageSize, total])

  useEffect(() => {
    if (!draftDigitalAssets || draftDigitalAssets.length === 0) {
      setSelectedRows([])
    }
  }, [draftDigitalAssets])

  useDidMount(() => {
    document_type = undefined
    status = Status.Active
    handleFilterChange({ document_type, status })
  })

  const columns: ColumnsType<DigitalAsset> = [
    {
      title: t('list.tableHeaders.asset_name'),
      dataIndex: 'asset_name',
      sorter: true,
      sortOrder: sort_field === 'asset_name' ? (sort_reversed ? 'descend' : 'ascend') : null,
    },
    {
      title: t('list.tableHeaders.description'),
      dataIndex: 'description',
      sorter: true,
      sortOrder: sort_field === 'description' ? (sort_reversed ? 'descend' : 'ascend') : null,
    },
    {
      title: t('list.tableHeaders.document_type'),
      dataIndex: 'document_type',
      sorter: true,
      sortOrder: sort_field === 'document_type' ? (sort_reversed ? 'descend' : 'ascend') : null,
      render: (_, record: DigitalAsset) => record.document_type || 'link',
    },
    {
      title: t('list.tableHeaders.company_name'),
      dataIndex: 'company_name',
      sorter: true,
      sortOrder: sort_field === 'company_name' ? (sort_reversed ? 'descend' : 'ascend') : null,
    },
    {
      title: t('list.tableHeaders.user_name'),
      dataIndex: 'user_name',
      sorter: true,
      sortOrder: sort_field === 'user_name' ? (sort_reversed ? 'descend' : 'ascend') : null,
    },
    {
      title: t('list.tableHeaders.upload_date'),
      dataIndex: 'created_at',
      sorter: true,
      sortOrder: sort_field === 'created_at' ? (sort_reversed ? 'descend' : 'ascend') : null,
      render: (_, record) => formatIsoDate(record.created_at),
    },
    {
      render: (_, record) => <DADropdownActions digitalAsset={record} canEditOrDelete={canEditOrDelete} />,
    },
  ]

  const handleFilterChange = useCallback(
    (newFilterValue: Partial<DigitalAssetListRequestFilter>) => {
      filterUpdated({ ...filterValue, ...newFilterValue })
    },
    [filterUpdated, filterValue],
  )

  const rowSelection: TableProps<DigitalAsset>['rowSelection'] = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: DigitalAsset[]) => {
      setSelectedRows(selectedRowKeys)
      selectedDigitalAssetDraftAction(
        selectedRows.map<DraftDigitalAsset>((asset) => {
          return {
            asset_type: asset.asset_type,
            id: asset.id,
            name: asset.document_name || asset.link_url,
          }
        }),
      )
    },
    preserveSelectedRowKeys: true,
    selectedRowKeys: selectedRows,
  }

  const rowKey: TableProps<DigitalAsset>['rowKey'] = (record) => record.id

  const handleTableChange: TableProps<DigitalAsset>['onChange'] = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    })

    // Handle sorting
    if (sorter && !Array.isArray(sorter) && sorter.field) {
      const sortField = sorter.field as keyof DigitalAsset
      const sortReversed = sorter.order === 'descend'
      sortingUpdated({ sort_field: sortField, sort_reversed: sortReversed })
    } else {
      // Clear sorting when no sorter is active
      sortingUpdated({ sort_field: undefined, sort_reversed: false })
    }

    listRequested({ page: pagination.current })
  }

  const onRowFunctions: TableProps<DigitalAsset>['onRow'] = (record, _rowIndex) => {
    if (setDigitalAssetDetail) {
      return {
        onClick: (_event) => {
          setDigitalAssetDetail(record)
        },
      }
    }
    return {}
  }

  return (
    <Table<DigitalAsset>
      columns={columns}
      dataSource={data}
      rowKey={rowKey}
      loading={progress === Progress.WORK}
      onChange={handleTableChange}
      pagination={tableParams.pagination}
      rowSelection={rowSelection}
      onRow={onRowFunctions}
      rowClassName={'row-cursor-pointer'}
      scroll={data && data.length > 0 && height ? { y: height } : {}}
    />
  )
}

export default DigitalAssetTable
